// @ts-nocheck
import CurrencySelector from '../../CurrencySelector/CurrencySelector';
import CheckoutCurrencyNotice from '../../CheckoutCurrencyNotice/CheckoutCurrencyNotice';
import { Box, Link, CircularProgress, Modal } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { EmbedSignDocument } from '@documenso/embed-react';
import { hasMixedPlans } from '../../../../utils/booking';
import { formatCurrency as formatCurrencyAmount } from '../../../../utils/currencyHelper';
import {
  getSelectedAddOnsTotal,
  groupSelectedAddOnSummaryItems
} from '../../../../utils/percentageAddOns';
import { XGCheckBox, XGButton, XGInput } from 'xg-cl';
import { useEffect, useState, useRef } from 'react';
import pdf from '../../../../assets/images/pdf.png';
import {
  setSignedDocumentToken,
  setSignedDocumentId,
  setDocumentStatus,
  setSignedDocumentError,
  setIsSignModalOpen,
  setRequiresSignedDocument,
  setCustomFormError,
  setIsTermsChecked
} from '../../../../redux/slices/checkout/checkout';
import {
  addAppliedDiscountCode,
  removeAppliedDiscountCode
} from '../../../../redux/slices/booking/booking';
import { useDocumentSocket } from '../../../../hooks/useDocumentSocket';
import { isEmailValid } from '../../../../utils/emailValidator';
import globalConfig from '../../../../config/globalConfig';
import { getTokenFromEsignatureProvider } from '../../../../services/booking/booking';
import { validateTripDiscountCode } from '../../../../services/discountcode/discountcode';
import { hasTripAssignedDiscountCodes } from '../../../../utils/discountCodes';
import * as PropTypes from 'prop-types';
import './BookingTotals.css';

/**
 * Functional React component for rendering a custom booking totals section.
 *
 * @namespace Components
 *
 * @param {Object} props - The component's properties
 * @param {string} [props.id] - The ID for the booking totals component.
 * @param {...any} props.rest - Additional props to be spread on the parent element.
 *
 * @returns {JSX.Element} React element representing the booking totals component.
 */

const BookingTotals = ({
  id,
  currencyState,
  operatorCurrency,
  paymentMethod,
  paymentMethodOrder,
  ...rest
}) => {
  const { t } = useTranslation(['trip', 'common', 'errors', 'bookings']);
  const {
    enableNewPaymentPlans,
    enablePercentageBasedAddons,
    enableDiscountCodes,
    enableMultiCurrency
  } = useFlags();
  const booking = useSelector((state) => state.booking);
  const bookingplans = booking?.bookingplans || [];
  const passengerForms = booking?.passengerForms || [];
  const addons = booking?.addons || [];
  const selectedAddOns = booking?.selectedAddOns || [];
  const appliedDiscountCodes = booking?.appliedDiscountCodes || [];
  const bookingTotals = booking?.bookingTotals || [];
  const [discountCodeInput, setDiscountCodeInput] = useState('');
  const [discountCodeError, setDiscountCodeError] = useState('');
  const [isValidatingDiscountCode, setIsValidatingDiscountCode] =
    useState(false);
  const sortedBookingTotals =
    bookingTotals && bookingTotals.length
      ? [...bookingTotals].sort((a, b) => a?.order - b?.order)
      : [];

  const includeMixedPlans = hasMixedPlans(bookingplans);
  const checkout = useSelector((state) => state.checkout) || {};
  const {
    customForm,
    customFormError,
    signedDocumentToken,
    signedDocumentError,
    isSignModalOpen,
    documentStatus,
    signedDocumentId,
    termsError,
    isTermsChecked
  } = checkout;

  // Calculate totals
  const selectedPlans = bookingplans.filter(
    (plan) => !isNaN(plan.selectedQuantity) && Number(plan.selectedQuantity) > 0
  );

  const totalPlansPrice = selectedPlans.reduce((total, plan) => {
    return total + plan.priceperperson * plan.selectedQuantity;
  }, 0);

  // Calculate add-ons total
  const totalAddOnsPrice = getSelectedAddOnsTotal({
    selectedAddOns,
    addons,
    passengers: passengerForms,
    bookingPlans: bookingplans,
    enablePercentageBasedAddons
  });
  const addOnSummaryItems = groupSelectedAddOnSummaryItems({
    selectedAddOns,
    addons,
    passengers: passengerForms,
    bookingPlans: bookingplans,
    enablePercentageBasedAddons,
    getPassengerLabel: (passengerCount) =>
      `${passengerCount} ${
        passengerCount === 1
          ? t('common:passengerLower', 'passenger')
          : t('common:passengersLower', 'passengers')
      }`
  });

  const totalTripPrice = totalPlansPrice + totalAddOnsPrice;
  const totalDiscountAmount = appliedDiscountCodes.reduce(
    (total, discountCode) => total + Number(discountCode?.discountAmount || 0),
    0
  );
  const discountedTripPrice = Math.max(totalTripPrice - totalDiscountAmount, 0);

  // Extract payment data from bookingTotals for direct control
  const paymentAmountItem = sortedBookingTotals.find(
    (item) => item.key === 'trip-subtotal' && item?.visible
  );
  const processingFeeItem = sortedBookingTotals.find(
    (item) => item.key === 'trip-totalfees' && item?.visible
  );
  const tripTotalItem = sortedBookingTotals.find(
    (item) => item.key === 'trip-total' && item?.visible
  );

  const paymentAmount = paymentAmountItem?.totalPrice || 0;
  const processingFee = processingFeeItem?.totalPrice || 0;
  const finalAmountToCharge =
    tripTotalItem?.totalPrice || paymentAmount + processingFee;

  const trip = booking?.trip || null;
  const shouldRenderDiscountCodes =
    enableDiscountCodes && hasTripAssignedDiscountCodes(trip);
  const requiresSignedDocument = trip?.requiresSignedDocument ?? false;
  const tenant = booking?.tenant || null;
  const tenantCurrency = tenant?.sellingcurrency || tenant?.basecurrency;
  const formatCurrency = (amount) =>
    formatCurrencyAmount(
      amount,
      operatorCurrency || tenantCurrency?.currencycode
        ? {
            currencyCode: operatorCurrency || tenantCurrency.currencycode,
            currencySymbol:
              tenantCurrency?.currencysymbol ||
              operatorCurrency ||
              tenantCurrency.currencycode
          }
        : {}
    );

  const shouldRequireTerms = Boolean(
    !requiresSignedDocument && tenant?.tcenabled
  );

  const checkoutLockedRef = useRef(false);
  checkoutLockedRef.current = Boolean(
    currencyState?.completed ||
    currencyState?.unresolved ||
    checkout.isSubmitting
  );

  const steps = booking?.steps || [];
  const activeStep = steps?.find((step) => step.active);
  const dispatch = useDispatch();

  const getDiscountErrorMessage = (error) => {
    const errorCode = error?.name || error?.errorCode;

    switch (errorCode) {
      case 'CODE_EXPIRED':
        return t('errors:discountCodeExpired');
      case 'CODE_UNAVAILABLE':
        return t('errors:discountCodeUnavailable');
      case 'CODE_NOT_COMBINABLE': {
        const conflictingCodeString =
          error?.details?.conflictingCodeString ||
          appliedDiscountCodes.find((discountCode) => !discountCode.combinable)
            ?.code ||
          appliedDiscountCodes[0]?.code ||
          '';

        return t('errors:discountCodeNotCombinable', {
          code: conflictingCodeString
        });
      }
      case 'CODE_NOT_FOUND':
      default:
        return t('errors:discountCodeNotValidForBooking');
    }
  };

  const handleApplyDiscountCode = async () => {
    if (
      currencyState?.completed ||
      currencyState?.unresolved ||
      checkout.isSubmitting
    )
      return;
    const code = discountCodeInput.trim();
    if (!code || isValidatingDiscountCode) return;

    const alreadyApplied = appliedDiscountCodes.some(
      (discountCode) => discountCode.code?.toUpperCase() === code.toUpperCase()
    );

    if (alreadyApplied) {
      setDiscountCodeError(t('errors:discountCodeAlreadyApplied'));
      return;
    }

    if (
      appliedDiscountCodes.length > 0 &&
      appliedDiscountCodes.some((discountCode) => !discountCode.combinable)
    ) {
      setDiscountCodeError(
        t('errors:discountCodeNotCombinable', {
          code: appliedDiscountCodes[0]?.code || ''
        })
      );
      return;
    }

    setIsValidatingDiscountCode(true);
    setDiscountCodeError('');

    try {
      const response = await validateTripDiscountCode(trip?.tripid, {
        code,
        eligibleSubtotal: totalTripPrice,
        baseTripAmount: totalPlansPrice,
        passengerCount: selectedPlans.reduce(
          (total, plan) => total + Number(plan?.selectedQuantity || 0),
          0
        ),
        existingDiscountCodeIds: appliedDiscountCodes.map(
          (discountCode) => discountCode.discountCodeId
        ),
        bookingId: null
      });

      if (checkoutLockedRef.current) return;
      const discountCode = response?.data;

      if (discountCode?.discountCodeId) {
        dispatch(addAppliedDiscountCode(discountCode));
        setDiscountCodeInput('');
        setDiscountCodeError('');
      }
    } catch (error) {
      setDiscountCodeError(getDiscountErrorMessage(error));
    } finally {
      setIsValidatingDiscountCode(false);
    }
  };

  const handleRemoveDiscountCode = (discountCodeId) => {
    if (
      currencyState?.completed ||
      currencyState?.unresolved ||
      checkout.isSubmitting
    )
      return;
    dispatch(removeAppliedDiscountCode({ discountCodeId }));
    setDiscountCodeError('');
  };

  // Initialize requiresSignedDocument from trip data
  useEffect(() => {
    const requires = trip?.requiresSignedDocument ?? false;
    dispatch(setRequiresSignedDocument(requires));
  }, [trip?.requiresSignedDocument, dispatch]);

  // WebSocket listener for document events
  useDocumentSocket(signedDocumentId, (event) => {
    if (event === 'DOCUMENT_SIGNED') {
      dispatch(setDocumentStatus('COMPLETED'));
    }
  });

  const validateCustomForm = () => {
    dispatch(
      setCustomFormError({
        email: '',
        firstName: '',
        lastName: ''
      })
    );

    let errors = { ...customFormError };

    if (customForm.email.trim() === '') {
      errors = { ...errors, email: t('trip:yourEmailIsRequired') };
    } else {
      if (!isEmailValid(customForm.email)) {
        errors = { ...errors, email: t('trip:pleaseEnterValidEmail') };
      }
    }

    if (customForm.firstName.trim() === '') {
      errors = { ...errors, firstName: t('trip:yourFirstNameIsRequired') };
    }

    if (customForm.lastName.trim() === '') {
      errors = { ...errors, lastName: t('trip:yourLastNameIsRequired') };
    }

    const isValidForm =
      errors.email === '' && errors.firstName === '' && errors.lastName === '';
    if (!isValidForm) {
      dispatch(setCustomFormError(errors));
    }

    return isValidForm;
  };

  const getSignedDocumentToken = async () => {
    const isValid = validateCustomForm();
    if (!isValid) {
      return;
    }

    if (signedDocumentToken) {
      return signedDocumentToken;
    }

    dispatch(setDocumentStatus('PENDING'));

    const data = {
      email: customForm?.email.trim(),
      name: `${customForm?.firstName.trim()} ${customForm?.lastName.trim()}`
    };

    try {
      const { token, documentId } = await getTokenFromEsignatureProvider(
        trip,
        data
      );
      dispatch(setSignedDocumentId(documentId));
      dispatch(setSignedDocumentToken(token));
      return token;
    } catch (error) {
      dispatch(setDocumentStatus('ERROR'));
    }
  };

  const handleSignDocument = async () => {
    const token = await getSignedDocumentToken();
    if (token) {
      dispatch(setIsSignModalOpen(true));
      dispatch(setSignedDocumentError(''));
    }
  };

  const handleDocumentCompleted = () => {
    dispatch(setDocumentStatus('COMPLETED'));
    dispatch(setIsSignModalOpen(false));
  };

  return (
    <Box
      className='booking-totals'
      data-testid='booking-totals'
      id={id}
      {...rest}
    >
      <div className='book-now-modal__section-header booking-totals__currency-header'>
        <span className='h4-semibold text-capitalize'>
          {t('trip:paymentSummary')}
        </span>
        {activeStep?.key === 'payment' &&
          currencyState &&
          (currencyState.availability ||
            currencyState.managed ||
            currencyState.loading) && (
            <CurrencySelector
              operatorCurrency={operatorCurrency}
              availability={currencyState.availability}
              selectedCurrency={
                currencyState.pendingSelection || currencyState.selection
              }
              showLoadingText={false}
              showRateLockMessage={false}
              loading={currencyState.loading}
              error={currencyState.error}
              invalidated={currencyState.quoteInvalidated}
              disabled={
                currencyState.completed ||
                currencyState.unresolved ||
                paymentMethod === 'us_bank_account' ||
                checkout.isSubmitting ||
                checkout.isProcessing
              }
              onCurrencyChange={currencyState.requestCurrency}
              onRefresh={currencyState.refresh}
            />
          )}
      </div>

      {activeStep?.key === 'payment' &&
        currencyState &&
        enableMultiCurrency &&
        paymentMethodOrder?.includes('us_bank_account') &&
        paymentMethod === 'us_bank_account' && (
          <span className='booking-totals__currency-helper'>
            {t('bookings:currencySelector.cardToChooseCurrency')}
          </span>
        )}
      {/* Total Trip Price Section */}
      <div className='booking-totals__items'>
        <div className='booking-total__item'>
          <div className='booking-total__item-column'>
            <span className='text-sm-bold booking-total__item-column__title'>
              {t('trip:totalTripPrice', 'Total trip price')}
            </span>
          </div>
          <div className='booking-total__item-column'>
            <span className='h3-semibold booking-total__item__line-total'>
              {formatCurrency(totalTripPrice)}
            </span>
          </div>
        </div>

        {/* Individual Plan Breakdowns */}
        {selectedPlans.map((plan) => {
          const { depositamount: deposit = 0, priceperperson = 0 } = plan;
          const depositamount =
            deposit > 0 ? deposit : includeMixedPlans ? priceperperson : 0;
          const totalPlanPrice = plan.priceperperson * plan.selectedQuantity;
          const totalDepositAmount = depositamount * plan.selectedQuantity;

          return (
            <div
              key={plan.tripplanid}
              className='booking-total__item booking-total__item--indented'
            >
              <div className='booking-total__item-column'>
                <span className='booking-totals__italic-sm-text'>
                  {plan.title} ({plan.selectedQuantity}{' '}
                  {plan.selectedQuantity === 1
                    ? t('common:passengerLower', 'passenger')
                    : t('common:passengersLower', 'passengers')}
                  )
                </span>
              </div>
              <div className='booking-total__item-column'>
                <span className='text-sm-medium'>
                  {formatCurrency(totalPlanPrice)}
                </span>
                {!enableNewPaymentPlans &&
                  totalDepositAmount > 0 &&
                  totalDepositAmount !== totalPlanPrice && (
                    <div
                      className='text-xs-medium text-muted--primary'
                      style={{ marginTop: '2px' }}
                    >
                      {t('trip:deposit', 'Deposit')}:{' '}
                      {formatCurrency(totalDepositAmount)}
                    </div>
                  )}
              </div>
            </div>
          );
        })}

        {/* Add-ons Breakdowns */}
        {addOnSummaryItems.map((item, index) => {
          return (
            <div
              key={`${item.addon?.tripaddonid}-${item.perPersonAmount ?? 'fixed'}-${index}`}
              className='booking-total__item booking-total__item--indented'
            >
              <div className='booking-total__item-column'>
                <span className='booking-totals__italic-sm-text'>
                  {item.title}
                </span>
              </div>
              <div className='booking-total__item-column'>
                <span className='text-sm-medium'>
                  {item.amount < 0 ? '-' : ''}
                  {formatCurrency(Math.abs(item.amount))}
                </span>
              </div>
            </div>
          );
        })}

        {appliedDiscountCodes.map((discountCode) => (
          <div
            key={discountCode.discountCodeId}
            className='booking-total__item booking-total__item--discount'
          >
            <div className='booking-total__item-column'>
              <span className='text-sm-regular booking-total__discount-label'>
                {t('trip:discountCodeLine', { code: discountCode.code })}
              </span>
            </div>
            <div className='booking-total__item-column'>
              <span className='text-sm-medium booking-total__discount-value'>
                -{formatCurrency(Number(discountCode.discountAmount || 0))}
              </span>
            </div>
          </div>
        ))}

        {totalDiscountAmount > 0 && (
          <div className='booking-total__item booking-total__item--amount-due'>
            <div className='booking-total__item-column'>
              <span className='text-sm-bold booking-total__item-column__title'>
                {t('trip:amountDue')}
              </span>
            </div>
            <div className='booking-total__item-column'>
              <span className='h3-semibold booking-total__item__line-total'>
                {formatCurrency(discountedTripPrice)}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* This Payment Section */}
      {(paymentAmount > 0 || processingFee > 0) && (
        <div className='booking-totals__this-payment'>
          <span className='booking-totals__divider' />
          <div className='booking-totals__payment-section'>
            <div className='booking-total__item booking-total__item--payment-header'>
              <div className='booking-total__item-column'>
                <span className='text-sm-bold booking-total__item-column__title'>
                  {t('trip:thisPayment')}
                </span>
              </div>
            </div>

            {/* Payment Amount - Hide if zero */}
            {paymentAmount > 0 && (
              <div className='booking-total__item booking-total__item-custom'>
                <div className='booking-total__item-column'>
                  <span className='text-sm-regular'>
                    {t('trip:tripSubTotal')}
                  </span>
                </div>
                <div className='booking-total__item-column'>
                  <span className='text-sm-bold'>
                    {formatCurrency(paymentAmount)}
                  </span>
                </div>
              </div>
            )}

            {/* Processing Fee - Hide if zero */}
            {processingFee > 0 && (
              <div className='booking-total__item booking-total__item-custom'>
                <div className='booking-total__item-column'>
                  <span className='text-sm-regular'>
                    {t('trip:processingFee', 'Processing fee')}
                  </span>
                </div>
                <div className='booking-total__item-column'>
                  <span className='text-sm-bold booking-total__item__line-total'>
                    {formatCurrency(processingFee)}
                  </span>
                </div>
              </div>
            )}

            {/* Amount to Charge */}
            <div className='booking-total__item booking-total__item-custom'>
              <div className='booking-total__item-column'>
                <span className='text-sm-regular booking-total__item-column__title'>
                  {t('trip:amountToCharge')}
                </span>
              </div>
              <div className='booking-total__item-column'>
                <span className='text-sm-bold booking-total__item__line-total'>
                  {formatCurrency(finalAmountToCharge)}
                </span>
              </div>
            </div>
            {activeStep?.key === 'payment' && currencyState && (
              <CheckoutCurrencyNotice
                currencyState={currencyState}
                operatorCurrency={operatorCurrency}
                showOperatorCurrency={false}
                inline
              />
            )}
          </div>
        </div>
      )}

      {activeStep?.key === 'payment' &&
        currencyState?.error &&
        paymentAmount <= 0 &&
        processingFee <= 0 && (
          <CheckoutCurrencyNotice
            currencyState={currencyState}
            operatorCurrency={operatorCurrency}
            showOperatorCurrency={false}
          />
        )}

      {selectedPlans?.length === 0 && (
        <span className='booking-totals__no-plans'>
          {t('trip:noPlansSelected')}
        </span>
      )}

      {activeStep?.key === 'payment' && (
        <>
          {shouldRenderDiscountCodes && (
            <div className='booking-totals__discount-code'>
              <span className='text-sm-bold booking-totals__discount-title'>
                {t('trip:discountCode')}
              </span>
              {appliedDiscountCodes.length > 0 && (
                <div className='booking-totals__discount-chips'>
                  {appliedDiscountCodes.map((discountCode) => (
                    <button
                      key={discountCode.discountCodeId}
                      type='button'
                      disabled={
                        currencyState?.completed ||
                        currencyState?.unresolved ||
                        checkout.isSubmitting
                      }
                      className='booking-totals__discount-chip'
                      onClick={() =>
                        handleRemoveDiscountCode(discountCode.discountCodeId)
                      }
                    >
                      <strong>{discountCode.code}</strong>
                      <span>
                        {`-${formatCurrency(Number(discountCode.discountAmount || 0))}`}
                      </span>
                      <span aria-hidden='true'>x</span>
                    </button>
                  ))}
                </div>
              )}
              <div className='booking-totals__discount-form'>
                <XGInput
                  id='booking-discount-code-input'
                  label={null}
                  name='booking-discount-code'
                  disabled={
                    currencyState?.completed ||
                    currencyState?.unresolved ||
                    checkout.isSubmitting
                  }
                  placeholder={t('trip:enterDiscountCode')}
                  type='text'
                  value={discountCodeInput}
                  onChange={(event) => {
                    setDiscountCodeInput(event?.target?.value ?? '');
                    setDiscountCodeError('');
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      handleApplyDiscountCode();
                    }
                  }}
                />
                <XGButton
                  id='booking-discount-code-apply'
                  size='medium'
                  variant='outlined'
                  type='secondary'
                  text={t('trip:apply')}
                  disabled={
                    !discountCodeInput.trim() ||
                    isValidatingDiscountCode ||
                    currencyState?.completed ||
                    checkout.isSubmitting
                  }
                  onClick={handleApplyDiscountCode}
                />
              </div>
              {discountCodeError && (
                <span className='booking-totals__discount-error text-xs-regular'>
                  {discountCodeError}
                </span>
              )}
            </div>
          )}
          {requiresSignedDocument ? (
            <>
              <div className='document-signing__wrapper'>
                <div className='document-signing__wrapper-text'></div>
                {documentStatus !== 'COMPLETED' && (
                  <div className='document-signing__actions'>
                    <Box className='document-signing__btn__wrapper'>
                      <XGButton
                        id='sign-document-button'
                        size='medium'
                        variant='outlined'
                        type='secondary'
                        text={t('bookings:signDocument')}
                        startIcon={<img src={pdf} alt='PDF' />}
                        disabled={
                          !customForm.email ||
                          !customForm.firstName ||
                          !customForm.lastName ||
                          documentStatus === 'PENDING'
                        }
                        onClick={handleSignDocument}
                      />
                      {documentStatus === 'PENDING' && (
                        <CircularProgress
                          size={24}
                          className='document-signing__progress'
                        />
                      )}
                    </Box>
                    {signedDocumentError !== '' && (
                      <span className='text-sm-regular document-sign__error'>
                        {signedDocumentError}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <Modal
                open={isSignModalOpen}
                onClose={() => dispatch(setIsSignModalOpen(false))}
                aria-labelledby='document-signing-modal'
              >
                <Box className='document-signing__modal'>
                  {signedDocumentToken && (
                    <EmbedSignDocument
                      host={globalConfig.eSignatureHost}
                      token={signedDocumentToken}
                      lockName
                      onDocumentCompleted={handleDocumentCompleted}
                      className='document-signing__embed'
                    />
                  )}
                </Box>
              </Modal>
            </>
          ) : shouldRequireTerms ? (
            <div className='terms-and-conditions__wrapper'>
              <div className='terms-and-conditions__content'>
                <XGCheckBox
                  id='check-terms-and-conditions'
                  name='terms'
                  size='medium'
                  checked={isTermsChecked}
                  onChange={() => {
                    dispatch(setIsTermsChecked(!isTermsChecked));
                  }}
                />
                <Trans i18nKey='trip:acceptTermsAndConditions'>
                  <Link
                    href={tenant?.tclink || ''}
                    target='_blank'
                    rel='noreferrer'
                  />
                </Trans>
              </div>
              {termsError !== '' && !isTermsChecked && (
                <span className='text-sm-regular terms-and-conditions__error'>
                  {termsError}
                </span>
              )}
            </div>
          ) : null}
        </>
      )}
    </Box>
  );
};

BookingTotals.propTypes = {
  id: PropTypes.string.isRequired,
  currencyState: PropTypes.object,
  operatorCurrency: PropTypes.string,
  paymentMethod: PropTypes.string,
  paymentMethodOrder: PropTypes.array
};

export default BookingTotals;
